import React, { useState, useEffect } from 'react';
import './Navbar.css';
import { isPushSupported, isPushSubscribed, subscribeToPush, unsubscribeFromPush } from '../services/pushNotifications';

interface DropboxStatus {
  connected: boolean;
  accountId?: string;
  email?: string;
  expiresAt?: string;
  createdAt?: string;
}

interface GarminStatus {
  connected: boolean;
  email?: string;
  sessionCreated?: string;
}

interface StravaStatus {
  connected: boolean;
  athleteId?: string;
  firstName?: string;
  lastName?: string;
  createdAt?: string;
}

interface BarrysStatus {
  connected: boolean;
  valid?: boolean;
  email?: string;
  createdAt?: string;
}

interface User {
  id: string;
  garminEmail: string;
  displayName: string;
  isAdmin: boolean;
}

interface NavbarProps {
  appName: string;
  appEmoji: string;
  dropboxStatus: DropboxStatus | null;
  garminStatus: GarminStatus | null;
  stravaStatus: StravaStatus | null;
  barrysStatus?: BarrysStatus | null;
  isCheckingDropbox: boolean;
  isCheckingGarmin: boolean;
  isCheckingStrava: boolean;
  isCheckingBarrys?: boolean;
  currentUser: User | null;
  onLogout: () => void;
  onDisconnectDropbox: () => void;
  onDisconnectStrava: () => void;
  onDisconnectBarrys?: () => void;
  onError?: (message: string) => void;
  onTestNotification?: () => void;
}

export const Navbar: React.FC<NavbarProps> = ({
  appName,
  appEmoji,
  dropboxStatus,
  garminStatus,
  stravaStatus,
  barrysStatus,
  isCheckingDropbox,
  isCheckingGarmin,
  isCheckingStrava,
  isCheckingBarrys,
  currentUser,
  onLogout,
  onDisconnectDropbox,
  onDisconnectStrava,
  onDisconnectBarrys,
  onError,
  onTestNotification,
}) => {
  const [showDropboxTooltip, setShowDropboxTooltip] = useState(false);
  const [showGarminTooltip, setShowGarminTooltip] = useState(false);
  const [showStravaTooltip, setShowStravaTooltip] = useState(false);
  const [showBarrysTooltip, setShowBarrysTooltip] = useState(false);
  const [dropboxPressTimer, setDropboxPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [stravaPressTimer, setStravaPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [barrysPressTimer, setBarrysPressTimer] = useState<NodeJS.Timeout | null>(null);
  const [dropboxCloseTimer, setDropboxCloseTimer] = useState<NodeJS.Timeout | null>(null);
  const [garminCloseTimer, setGarminCloseTimer] = useState<NodeJS.Timeout | null>(null);
  const [stravaCloseTimer, setStravaCloseTimer] = useState<NodeJS.Timeout | null>(null);
  const [barrysCloseTimer, setBarrysCloseTimer] = useState<NodeJS.Timeout | null>(null);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushSubscribed, setPushSubscribed] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);

  useEffect(() => {
    if (isPushSupported()) {
      setPushSupported(true);
      isPushSubscribed().then(setPushSubscribed);
    }
  }, []);

  const handlePushToggle = async () => {
    setPushLoading(true);
    try {
      if (pushSubscribed) {
        await unsubscribeFromPush();
        setPushSubscribed(false);
      } else {
        await subscribeToPush();
        setPushSubscribed(true);
      }
    } catch (err) {
      console.error('Push notification toggle failed:', err);
      onError?.('Failed to toggle push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const getDropboxStatus = () => {
    if (isCheckingDropbox) return 'loading';
    if (!dropboxStatus || !dropboxStatus.connected) return 'disconnected';
    return 'connected';
  };

  const getGarminStatus = () => {
    if (isCheckingGarmin) return 'loading';
    if (!garminStatus) return 'disconnected';
    if (!garminStatus.connected) return 'disconnected';
    return 'connected';
  };

  const getStravaStatus = () => {
    if (isCheckingStrava) return 'loading';
    if (!stravaStatus || !stravaStatus.connected) return 'disconnected';
    return 'connected';
  };

  const getBarrysStatus = () => {
    if (isCheckingBarrys) return 'loading';
    if (!barrysStatus || !barrysStatus.connected) return 'disconnected';
    if (!barrysStatus.valid) return 'warning';
    return 'connected';
  };

  // Long-press handlers for mobile
  const handleDropboxPressStart = () => {
    if (dropboxStatus?.connected) {
      const timer = setTimeout(() => {
        onDisconnectDropbox();
      }, 3000);
      setDropboxPressTimer(timer);
    }
  };

  const handleDropboxPressEnd = () => {
    if (dropboxPressTimer) {
      clearTimeout(dropboxPressTimer);
      setDropboxPressTimer(null);
    }
  };

  const handleStravaPressStart = () => {
    if (stravaStatus?.connected) {
      const timer = setTimeout(() => {
        onDisconnectStrava();
      }, 3000);
      setStravaPressTimer(timer);
    }
  };

  const handleStravaPressEnd = () => {
    if (stravaPressTimer) {
      clearTimeout(stravaPressTimer);
      setStravaPressTimer(null);
    }
  };

  const handleBarrysPressStart = () => {
    if (barrysStatus?.connected && onDisconnectBarrys) {
      const timer = setTimeout(() => {
        onDisconnectBarrys();
      }, 3000);
      setBarrysPressTimer(timer);
    }
  };

  const handleBarrysPressEnd = () => {
    if (barrysPressTimer) {
      clearTimeout(barrysPressTimer);
      setBarrysPressTimer(null);
    }
  };

  // Tooltip handlers with delay
  const handleDropboxEnter = () => {
    if (dropboxCloseTimer) {
      clearTimeout(dropboxCloseTimer);
      setDropboxCloseTimer(null);
    }
    setShowDropboxTooltip(true);
  };

  const handleDropboxLeave = () => {
    const timer = setTimeout(() => {
      setShowDropboxTooltip(false);
    }, 150);
    setDropboxCloseTimer(timer);
  };

  const handleGarminEnter = () => {
    if (garminCloseTimer) {
      clearTimeout(garminCloseTimer);
      setGarminCloseTimer(null);
    }
    setShowGarminTooltip(true);
  };

  const handleGarminLeave = () => {
    const timer = setTimeout(() => {
      setShowGarminTooltip(false);
    }, 150);
    setGarminCloseTimer(timer);
  };

  const handleStravaEnter = () => {
    if (stravaCloseTimer) {
      clearTimeout(stravaCloseTimer);
      setStravaCloseTimer(null);
    }
    setShowStravaTooltip(true);
  };

  const handleStravaLeave = () => {
    const timer = setTimeout(() => {
      setShowStravaTooltip(false);
    }, 150);
    setStravaCloseTimer(timer);
  };

  const handleBarrysEnter = () => {
    if (barrysCloseTimer) {
      clearTimeout(barrysCloseTimer);
      setBarrysCloseTimer(null);
    }
    setShowBarrysTooltip(true);
  };

  const handleBarrysLeave = () => {
    const timer = setTimeout(() => {
      setShowBarrysTooltip(false);
    }, 150);
    setBarrysCloseTimer(timer);
  };

  return (
    <nav className="navbar">
      <div className="navbar__content">
        {/* App Name */}
        <div className="navbar__brand">
          <span className="navbar__emoji">{appEmoji}</span>
          <h1 className="navbar__title">{appName}</h1>
        </div>

        {/* Connection Status Indicators */}
        <div className="navbar__connections">
          <div className="navbar__connection" onMouseEnter={handleGarminEnter} onMouseLeave={handleGarminLeave}>
            <div className={`navbar__connection-indicator navbar__connection-indicator--${getGarminStatus()}`}>
              <span className="navbar__connection-icon">🏃</span>
              <span className="navbar__connection-label">Garmin</span>
            </div>
            {showGarminTooltip && (
              <div className="navbar__tooltip" onMouseEnter={handleGarminEnter} onMouseLeave={handleGarminLeave}>
                <div className="navbar__tooltip-header">
                  <span>🏃 Garmin Connect</span>
                  <span className={`navbar__tooltip-status navbar__tooltip-status--${getGarminStatus()}`}>
                    {getGarminStatus() === 'connected'
                      ? 'Connected'
                      : getGarminStatus() === 'loading'
                        ? 'Loading...'
                        : 'Disconnected'}
                  </span>
                </div>
                {garminStatus?.connected && garminStatus.email && (
                  <div className="navbar__tooltip-details">
                    <div className="navbar__tooltip-detail">
                      <span className="navbar__tooltip-label">Account:</span>
                      <span className="navbar__tooltip-value">{garminStatus.email}</span>
                    </div>
                    {garminStatus.sessionCreated && (
                      <div className="navbar__tooltip-detail">
                        <span className="navbar__tooltip-label">Connected:</span>
                        <span className="navbar__tooltip-value">
                          {new Date(garminStatus.sessionCreated).toLocaleDateString()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
                {!garminStatus?.connected && (
                  <div className="navbar__tooltip-warning">Garmin session expired — sign in again to reconnect.</div>
                )}
              </div>
            )}
          </div>

          <div className="navbar__connection" onMouseEnter={handleDropboxEnter} onMouseLeave={handleDropboxLeave}>
            <div
              className={`navbar__connection-indicator navbar__connection-indicator--${getDropboxStatus()}`}
              onTouchStart={handleDropboxPressStart}
              onTouchEnd={handleDropboxPressEnd}
              onTouchCancel={handleDropboxPressEnd}
            >
              <span className="navbar__connection-icon">📁</span>
              <span className="navbar__connection-label">Dropbox</span>
            </div>
            {showDropboxTooltip && (
              <div className="navbar__tooltip" onMouseEnter={handleDropboxEnter} onMouseLeave={handleDropboxLeave}>
                <div className="navbar__tooltip-header">
                  <span>📁 Dropbox</span>
                  <span className={`navbar__tooltip-status navbar__tooltip-status--${getDropboxStatus()}`}>
                    {getDropboxStatus() === 'connected'
                      ? 'Connected'
                      : getDropboxStatus() === 'loading'
                        ? 'Loading...'
                        : 'Disconnected'}
                  </span>
                </div>
                {dropboxStatus?.connected && (
                  <>
                    <div className="navbar__tooltip-details">
                      {dropboxStatus.email && (
                        <div className="navbar__tooltip-detail">
                          <span className="navbar__tooltip-label">Account:</span>
                          <span className="navbar__tooltip-value">{dropboxStatus.email}</span>
                        </div>
                      )}
                      {dropboxStatus.createdAt && (
                        <div className="navbar__tooltip-detail">
                          <span className="navbar__tooltip-label">Connected:</span>
                          <span className="navbar__tooltip-value">
                            {new Date(dropboxStatus.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <button onClick={onDisconnectDropbox} className="navbar__tooltip-disconnect">
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          <div className="navbar__connection" onMouseEnter={handleStravaEnter} onMouseLeave={handleStravaLeave}>
            <div
              className={`navbar__connection-indicator navbar__connection-indicator--${getStravaStatus()}`}
              onTouchStart={handleStravaPressStart}
              onTouchEnd={handleStravaPressEnd}
              onTouchCancel={handleStravaPressEnd}
            >
              <span className="navbar__connection-icon">🚴</span>
              <span className="navbar__connection-label">Strava</span>
            </div>
            {showStravaTooltip && (
              <div className="navbar__tooltip" onMouseEnter={handleStravaEnter} onMouseLeave={handleStravaLeave}>
                <div className="navbar__tooltip-header">
                  <span>🚴 Strava</span>
                  <span className={`navbar__tooltip-status navbar__tooltip-status--${getStravaStatus()}`}>
                    {getStravaStatus() === 'connected'
                      ? 'Connected'
                      : getStravaStatus() === 'loading'
                        ? 'Loading...'
                        : 'Disconnected'}
                  </span>
                </div>
                {stravaStatus?.connected && (
                  <>
                    <div className="navbar__tooltip-details">
                      {stravaStatus.firstName && stravaStatus.lastName && (
                        <div className="navbar__tooltip-detail">
                          <span className="navbar__tooltip-label">Athlete:</span>
                          <span className="navbar__tooltip-value">
                            {stravaStatus.firstName} {stravaStatus.lastName}
                          </span>
                        </div>
                      )}
                      {stravaStatus.createdAt && (
                        <div className="navbar__tooltip-detail">
                          <span className="navbar__tooltip-label">Connected:</span>
                          <span className="navbar__tooltip-value">
                            {new Date(stravaStatus.createdAt).toLocaleDateString()}
                          </span>
                        </div>
                      )}
                    </div>
                    <button onClick={onDisconnectStrava} className="navbar__tooltip-disconnect">
                      Disconnect
                    </button>
                  </>
                )}
              </div>
            )}
          </div>

          {barrysStatus !== undefined && (
            <div className="navbar__connection" onMouseEnter={handleBarrysEnter} onMouseLeave={handleBarrysLeave}>
              <div
                className={`navbar__connection-indicator navbar__connection-indicator--${getBarrysStatus()}`}
                onTouchStart={handleBarrysPressStart}
                onTouchEnd={handleBarrysPressEnd}
                onTouchCancel={handleBarrysPressEnd}
              >
                <span className="navbar__connection-icon">💪</span>
                <span className="navbar__connection-label">Barry&apos;s</span>
              </div>
              {showBarrysTooltip && (
                <div className="navbar__tooltip" onMouseEnter={handleBarrysEnter} onMouseLeave={handleBarrysLeave}>
                  <div className="navbar__tooltip-header">
                    <span>💪 Barry&apos;s Bootcamp</span>
                    <span className={`navbar__tooltip-status navbar__tooltip-status--${getBarrysStatus()}`}>
                      {getBarrysStatus() === 'connected'
                        ? 'Connected'
                        : getBarrysStatus() === 'loading'
                          ? 'Loading...'
                          : getBarrysStatus() === 'warning'
                            ? 'Session Expired'
                            : 'Disconnected'}
                    </span>
                  </div>
                  {barrysStatus?.connected && (
                    <>
                      <div className="navbar__tooltip-details">
                        {barrysStatus.email && (
                          <div className="navbar__tooltip-detail">
                            <span className="navbar__tooltip-label">Account:</span>
                            <span className="navbar__tooltip-value">{barrysStatus.email}</span>
                          </div>
                        )}
                        {barrysStatus.createdAt && (
                          <div className="navbar__tooltip-detail">
                            <span className="navbar__tooltip-label">Connected:</span>
                            <span className="navbar__tooltip-value">
                              {new Date(barrysStatus.createdAt).toLocaleDateString()}
                            </span>
                          </div>
                        )}
                      </div>
                      {onDisconnectBarrys && (
                        <button onClick={onDisconnectBarrys} className="navbar__tooltip-disconnect">
                          Disconnect
                        </button>
                      )}
                    </>
                  )}
                  {barrysStatus?.connected && !barrysStatus.valid && (
                    <div className="navbar__tooltip-warning">
                      Session expired. Please reconnect from the Connections panel.
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* User info and Actions */}
        <div className="navbar__actions">
          {currentUser && <span className="navbar__user-email">{currentUser.displayName}</span>}
          {pushSupported && (
            <button
              onClick={handlePushToggle}
              disabled={pushLoading}
              className={`navbar__button navbar__button--notifications${pushSubscribed ? ' navbar__button--notifications-active' : ''}`}
              title={pushSubscribed ? 'Disable notifications' : 'Enable notifications'}
            >
              <span className="navbar__button-icon">{pushSubscribed ? '🔔' : '🔕'}</span>
            </button>
          )}
          {onTestNotification && (
            <button onClick={onTestNotification} className="navbar__button" title="Test booking notification">
              <span className="navbar__button-icon">🧪</span>
            </button>
          )}
          <button onClick={onLogout} className="navbar__button navbar__button--logout">
            <span className="navbar__button-icon">🚪</span>
            <span className="navbar__button-text">Logout</span>
          </button>
        </div>
      </div>
    </nav>
  );
};
